import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { captureException, trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /** When true, shows a compact inline fallback suitable for panels */
  inline?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Detect chunk/module load failures that happen when a lazy-loaded chunk
 * is missing (e.g. after a deploy with new hashes).
 */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message?.toLowerCase() ?? ''
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    error.name === 'ChunkLoadError'
  )
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
    const boundary = this.props.inline ? 'panel' : 'page'
    if (isChunkLoadError(error)) {
      // Stale tab after a deploy, self-healing via the reload prompt below.
      // A product event keeps the rollout rate visible without spending the
      // bounded exception budget on a non-defect.
      trackEvent(EVENTS.CHUNK_LOAD_FAILED, { boundary })
      return
    }
    captureException(error, {
      boundary,
      // The top of the stack locates the crash; a deep tree can run to
      // kilobytes past that.
      component_stack: errorInfo.componentStack?.slice(0, 2000) ?? null,
    })
  }

  render() {
    if (!this.state.hasError) return this.props.children
    if (this.props.fallback) return this.props.fallback

    const chunkError = isChunkLoadError(this.state.error!)

    if (this.props.inline) {
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {chunkError ? 'Failed to load panel' : 'Something went wrong'}
            </p>
            <button
              onClick={() =>
                chunkError
                  ? window.location.reload()
                  : this.setState({ hasError: false, error: null })
              }
              className="mt-2 text-xs text-primary underline-offset-2 hover:underline"
            >
              {chunkError ? 'Reload page' : 'Retry'}
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-foreground">
            {chunkError ? 'Update available' : 'Something went wrong'}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {chunkError
              ? 'A new version has been deployed. Reload to get the latest.'
              : (this.state.error?.message ?? 'An unexpected error occurred.')}
          </p>
          <Button
            className="mt-4"
            onClick={() =>
              chunkError
                ? window.location.reload()
                : this.setState({ hasError: false, error: null })
            }
          >
            {chunkError ? 'Reload page' : 'Try again'}
          </Button>
        </div>
      </div>
    )
  }
}
